/**
 * MarketingShell — full chrome wrapper for marketing-website pages.
 *
 * Composes: skip-link → <ScrollToTop /> → <MarketingHeader /> → <main> →
 * <MarketingFooter />. The body of every marketing page is rendered via
 * the `children` prop (the createMarketingApp factory in Phase D passes
 * <Outlet /> here).
 *
 * Layout uses utility composition (flex-col + min-h-screen + flex-grow on
 * <main>) so the footer sits at the bottom of short pages without sticky
 * positioning.
 *
 * @module @puffin-core/marketing-shell/MarketingShell
 */

import PropTypes from 'prop-types';
import MarketingHeader from './MarketingHeader.jsx';
import MarketingFooter from './MarketingFooter.jsx';
import ScrollToTop from './ScrollToTop.jsx';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';

/**
 * @param {Object} props
 * @param {string} props.appKey            brand-assets key (e.g. 'parcel').
 * @param {Object} props.headerProps       Props passed to <MarketingHeader>.
 * @param {Object} props.footerProps       Props passed to <MarketingFooter>.
 * @param {React.ReactNode} props.children Page body (Outlet, in router-mode).
 *
 * The skip link's words are marketing-shell's own (`marketingShell` namespace, `skipLink`), not a
 * prop (LIFE-SHORTLISTED-001 F-Q7-1).
 */
export default function MarketingShell({ appKey, headerProps, footerProps, children }) {
  const t = useMarketingShellTranslate();
  return (
    <div className="flex flex-col min-h-screen">
      <a href="#main-content" className="skip-link">
        {t('skipLink')}
      </a>
      <ScrollToTop />
      <MarketingHeader appKey={appKey} {...headerProps} />
      <main id="main-content" className="flex-grow">
        {children}
      </main>
      <MarketingFooter appKey={appKey} {...footerProps} />
    </div>
  );
}

MarketingShell.propTypes = {
  appKey: PropTypes.string.isRequired,
  headerProps: PropTypes.object.isRequired,
  footerProps: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};
